import React, { useState, useEffect } from 'react';
import {
    Modal, Form, Input, Switch,
    message, Typography, Space,
    Row, Col, Select, Button
} from 'antd';
import {
    PrinterOutlined, 
    FileExcelOutlined, ApiOutlined, LinkOutlined, KeyOutlined,
    SyncOutlined
} from '@ant-design/icons';
import api from '../../../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const PrinterModal = ({
    visible,
    editingPrinter,
    loading,
    onOk,
    onCancel
}) => {
    const [form] = Form.useForm();
    const [currentFulfillMode, setCurrentFulfillMode] = useState('sheet');
    const [syncMappingLoading, setSyncMappingLoading] = useState(false);
    

    // Initialize form when editing printer changes
    useEffect(() => {
        if (editingPrinter) {
            const fulfillMode = editingPrinter.fulfillMode || 'sheet';
            setCurrentFulfillMode(fulfillMode);
            form.setFieldsValue({
                name: editingPrinter.name,
                isActive: editingPrinter.isActive,
                code: editingPrinter.code,
                sheetId: editingPrinter.sheetId,
                sheetName: editingPrinter.sheetName,
                printerProductSheetId: editingPrinter.printerProductSheetId,
                fulfillMode: fulfillMode,
                apiKey: editingPrinter.apiKey || '',
                attributes: editingPrinter.attributes ? JSON.stringify(editingPrinter.attributes, null, 2) : ''
            });
        } else {
            // Reset form for new printer
            form.resetFields();
            setCurrentFulfillMode('sheet');
        }
    }, [editingPrinter, form]);

    // Function to extract Google Sheet ID from URL
    const extractSheetId = (url) => {
        if (!url) return '';

        // Remove whitespace
        url = url.trim();

        // If it's already just an ID (no slashes or dots), return as is
        if (!/[\/\.]/.test(url)) {
            return url;
        }

        // Google Sheets URL patterns:
        // https://docs.google.com/spreadsheets/d/SHEET_ID/edit...
        // https://docs.google.com/spreadsheets/d/SHEET_ID/edit?gid=0#gid=0
        // https://docs.google.com/spreadsheets/d/SHEET_ID/...
        const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);

        if (match) {
            return match[1]; // Return the extracted Sheet ID
        }

        // If no match found, try alternative patterns or return original
        // Handle edge cases like direct sheet ID with extra characters
        const cleanId = url.replace(/[^a-zA-Z0-9-_]/g, '');

        // Validate if it looks like a Google Sheet ID (typically 44 characters)
        if (cleanId.length >= 20 && cleanId.length <= 50) {
            return cleanId;
        }

        return url; // Return original if can't extract
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            // Extract Sheet ID from URL if provided
            if (values.sheetId) {
                values.sheetId = extractSheetId(values.sheetId);
            }

            // Extract Printer Product Sheet ID from URL if provided
            if (values.printerProductSheetId) {
                values.printerProductSheetId = extractSheetId(values.printerProductSheetId);
            }

            // Parse attributes JSON if provided
            let parsedAttributes = null;
            if (values.attributes && values.attributes.trim()) {
                try {
                    parsedAttributes = JSON.parse(values.attributes);
                } catch (error) {
                    message.error('Attributes must be valid JSON');
                    return;
                }
            }

            const printerData = {
                ...values,
                attributes: parsedAttributes
            };

            await onOk(printerData, editingPrinter);
        } catch (error) {
            // Form validation failed
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setCurrentFulfillMode('sheet');
        onCancel();
    };

    const handleFulfillModeChange = (value) => {
        setCurrentFulfillMode(value);
        // Clear apiKey when switching to sheet mode
        if (value === 'sheet') {
            form.setFieldsValue({ apiKey: '' });
        }
        // Clear sheetId and sheetName when switching to api mode
        if (value === 'api') {
            form.setFieldsValue({ sheetId: '', sheetName: '' });
        }
    };

    // Handle sync SKU mapping from printerProductSheetId
    const handleSyncMapping = async () => {
        try {
            if (!editingPrinter?.id) {
                message.error('Please save the printer first before syncing mapping');
                return;
            }

            if (!editingPrinter?.printerProductSheetId) {
                message.error('Printer Product Mapping Sheet ID is required for syncing');
                return;
            }

            if (!editingPrinter?.code) {
                message.error('Printer code is required for syncing');
                return;
            }

            setSyncMappingLoading(true);

            // API call to sync SKU mapping - call to main worker via back end
            const response = await api.post('/printers/sync-mapping', {
                printerId: editingPrinter.id,
                printerCode: editingPrinter.code,
                printerProductSheetId: editingPrinter.printerProductSheetId
            });

            if (response.data.success) {
                message.success(`Mapping sync completed! ${response.data.data.totalSynced} mapping rules synced.`);
            } else {
                message.error('Mapping sync failed: ' + response.data.message);
            }

        } catch (error) {
            console.error('Mapping sync error:', error);
            message.error('Failed to sync mapping: ' + (error.response?.data?.message || error.message));
        } finally {
            setSyncMappingLoading(false);
        }
    };

    return (
        <Modal
            title={editingPrinter ? 'Edit Printer' : 'Add New Printer'}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={editingPrinter ? 'Update' : 'Create'}
            cancelText="Cancel"
            width={900}
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                className="printer-form"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="Printer Name"
                            rules={[{ required: true, message: 'Please enter printer name' }]}
                        >
                            <Input placeholder="Enter printer name" prefix={<PrinterOutlined />} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="code"
                            label="Printer Code"
                        >
                            <Input placeholder="e.g. PRINTER001" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="fulfillMode"
                            label="Order Fulfillment Mode"
                            initialValue="sheet"
                            rules={[{ required: true, message: 'Please select fulfillment mode' }]}
                        >
                            <Select 
                                placeholder="Select fulfillment mode"
                                onChange={handleFulfillModeChange}
                            >
                                <Option value="sheet">
                                    <Space>
                                        <FileExcelOutlined style={{ color: '#52c41a' }} />
                                        Google Sheet
                                    </Space>
                                </Option>
                                <Option value="api">
                                    <Space>
                                        <ApiOutlined style={{ color: '#1890ff' }} />
                                        Printer API
                                    </Space>
                                </Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {currentFulfillMode === 'sheet' ? (
                            <Form.Item
                                name="sheetId"
                                label="Google Sheet URL/ID"
                                tooltip="Paste Google Sheet URL or Sheet ID. System will automatically extract ID from URL"
                            >
                                <Input
                                    placeholder="https://docs.google.com/spreadsheets/d/SHEET_ID/... or just SHEET_ID"
                                    prefix={<LinkOutlined />}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                name="apiKey"
                                label="API Key"
                                tooltip="Enter the API key provided by the printer for integration"
                                rules={currentFulfillMode === 'api' ? [
                                    { required: true, message: 'API Key is required for Printer API mode' }
                                ] : []}
                            >
                                <Input.Password
                                    placeholder="Enter API key for printer integration"
                                    prefix={<KeyOutlined />}
                                />
                            </Form.Item>
                        )}
                    </Col>
                </Row>

                {currentFulfillMode === 'sheet' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="sheetName"
                                label="Sheet Name"
                                tooltip="The name/title of the Google Sheet (optional)"
                            >
                                <Input
                                    placeholder="Enter sheet name (e.g. Order Processing Sheet)"
                                    prefix={<FileExcelOutlined />}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                <Row gutter={10}>
                    <Col span={19}>
                        <Form.Item
                            name="printerProductSheetId"
                            label="Printer Product Mapping Sheet URL/ID"
                            tooltip="Google Sheet for printer product SKU mapping. Paste Google Sheet URL or Sheet ID for mapping configurations."
                        >
                            <Input
                                placeholder="https://docs.google.com/spreadsheets/d/SHEET_ID/... or just SHEET_ID"
                                prefix={<LinkOutlined />}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={5}>
                        <Form.Item label=" " style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                icon={<SyncOutlined />}
                                loading={syncMappingLoading}
                                onClick={handleSyncMapping}
                                disabled={!editingPrinter?.id || !editingPrinter?.printerProductSheetId || !editingPrinter?.code}
                                style={{ marginTop: 0, width: '100%' }}
                            >
                                {syncMappingLoading ? 'Syncing...' : 'Sync Mapping'}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>



                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="isActive"
                            label="Status"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="attributes"
                            label="Additional Attributes (JSON)"
                            tooltip="Enter JSON data for additional printer attributes"
                        >
                            <TextArea
                                rows={4}
                                placeholder={'{\n  "printerType": "Digital",\n  "capacity": "1000 prints/day",\n  "specialties": ["Business Cards", "Brochures"]\n}'}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default PrinterModal;
